import {Container, Graphics, Point, Text} from 'pixi.js';

/**
 * The grid: named cells over the SKY portion of the 800×600 stage (y 0–480).
 * The grass/dirt below is scenery, not callable. Cells are named a1 (top-left)
 * onward; canonical form is lowercase. Density comes from the difficulty:
 * more, tinier cells = harder to call and lead.
 */
export const GRID_H = 480; // the sky
export const LETTERS = 'abcdefghij';
const CELL_RE = new RegExp('^([' + LETTERS + '])\\s*-?\\s*(\\d)$');

// The survival ladder's rungs — density climbs, then insane adds speed.
export const DIFFICULTIES = {
  easy: {cols: 4, rows: 3, speed: 1, labelSize: 24},
  medium: {cols: 6, rows: 4, speed: 1, labelSize: 18},
  hard: {cols: 8, rows: 5, speed: 2, labelSize: 14},
  insane: {cols: 10, rows: 6, speed: 2, labelSize: 11} // round 11+
};

let COLS = 4;
let ROWS = 3;
let CELL_W = 200;
let CELL_H = 160;
let LABEL_SIZE = 24;

/** Configure the grid from a difficulty name; returns the difficulty config. */
export function setDifficulty(name) {
  const d = DIFFICULTIES[name] || DIFFICULTIES.easy;
  COLS = d.cols;
  ROWS = d.rows;
  CELL_W = 800 / COLS;
  CELL_H = GRID_H / ROWS;
  LABEL_SIZE = d.labelSize;
  return d;
}

export function cellName(col, row) {
  return LETTERS[col] + (row + 1);
}

/** Forgiving parse: "B2", " b-2 ", "b2" → "b2"; out-of-grid / junk → null. */
export function parseCell(input) {
  const m = String(input || '').trim().toLowerCase().match(CELL_RE);
  if (!m) {
    return null;
  }
  const col = LETTERS.indexOf(m[1]);
  const row = Number(m[2]) - 1;
  if (col < 0 || col >= COLS || row < 0 || row >= ROWS) {
    return null;
  }
  return m[1] + m[2];
}

/** Which cell a stage-space point is in, or null (below the sky). */
export function cellAt(point) {
  const col = Math.floor(point.x / CELL_W);
  const row = Math.floor(point.y / CELL_H);
  if (col < 0 || col >= COLS || row < 0 || row >= ROWS) {
    return null;
  }
  return cellName(col, row);
}

export function cellCenter(cell) {
  const col = LETTERS.indexOf(cell[0]);
  const row = Number(cell.slice(1)) - 1;
  return new Point((col + 0.5) * CELL_W, (row + 0.5) * CELL_H);
}

/**
 * GridOverlay
 * Faint gridlines + cell labels, styled to sit quietly over the NES sky.
 * Build AFTER setDifficulty().
 */
export class GridOverlay extends Container {
  constructor() {
    super();

    const lines = new Graphics();
    for (let c = 1; c < COLS; c++) {
      lines.moveTo(c * CELL_W, 0).lineTo(c * CELL_W, GRID_H);
    }
    for (let r = 1; r <= ROWS; r++) {
      lines.moveTo(0, r * CELL_H).lineTo(COLS * CELL_W, r * CELL_H);
    }
    lines.stroke({width: 2, color: 0xffffff, alpha: 0.13});
    this.addChild(lines);

    for (let c = 0; c < COLS; c++) {
      for (let r = 0; r < ROWS; r++) {
        const label = new Text({
          text: cellName(c, r).toUpperCase(),
          style: {fontFamily: 'Courier New, monospace', fontSize: LABEL_SIZE, fontWeight: '700', fill: 0xffffff}
        });
        label.alpha = 0.3;
        label.position.set(c * CELL_W + 7, r * CELL_H + 5);
        this.addChild(label);
      }
    }
  }
}
